import {isFilled} from '@prismicio/client'
import type {EventDocument, EventDocumentData} from 'prismicio-types'
import {readCookie} from 'src/common/CookiesContext'

export const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})
}

const formatTime = (dateString: string) => {
  const date = new Date(dateString)

  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })

  return formatter.format(date)
}

const formatDurationMinutes = (minutes: number) => {
  if (minutes < 60) {
    return `${minutes}min`
  }

  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`
}

const calculateDuration = (startDate: string, endDate: string) => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const diffMs = end.getTime() - start.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  return diffMins
}

export const getDuration = (data: EventDocument['data']) => {
  if (!isFilled.timestamp(data.event_date)) {
    return null
  }

  const isOnDemand = data.event_type === 'On demand'

  if (isOnDemand && isFilled.timestamp(data.event_finish_date)) {
    const startDate = new Date(data.event_date)
    const finishDate = new Date(data.event_finish_date)

    if (finishDate > startDate) {
      const durationMinutes = calculateDuration(data.event_date, data.event_finish_date)
      return formatDurationMinutes(durationMinutes)
    }

    return null
  }

  // For non-on-demand events, return formatted start time
  return formatTime(data.event_date)
}

// Such format doesn't have autoplay
export const convertContrastVideoWithoutAutoplay = (url: string): string => url.replace('/videos/', '/embed/')

export const getBackgroundImage = (data: EventDocumentData) =>
  // eslint-disable-next-line no-nested-ternary -- To select background image based on priority
  isFilled.image(data.contrast_video_preview_image)
    ? data.contrast_video_preview_image
    : isFilled.image(data.hero_image)
      ? data.hero_image
      : null

const VIDEO_UNLOCKED_COOKIE = 'video-unlocked'
const ONE_DAY_IN_SECONDS = 86400

// Create a simple hash from a string for cookie name
const createSimpleHash = (str: string): string => {
  let hash = 0

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }

  return Math.abs(hash).toString(36)
}

// Generate video-specific cookie name
const getVideoCookieName = (videoUrl: string): string => {
  const urlHash = createSimpleHash(videoUrl)

  return `${VIDEO_UNLOCKED_COOKIE}-${urlHash}`
}

export const setVideoUnlockedCookie = (videoUrl: string) => {
  if (typeof document === 'undefined') {
    return
  }

  const cookieName = getVideoCookieName(videoUrl)
  const cookieOptions = `path=/;max-age=${ONE_DAY_IN_SECONDS}`
  document.cookie = `${cookieName}=${encodeURIComponent('true')};${cookieOptions}`
}

export const getVideoUnlockedCookie = (videoUrl: string): boolean => {
  if (typeof document === 'undefined') {
    return false
  }

  const cookieName = getVideoCookieName(videoUrl)
  return readCookie(cookieName) === 'true'
}
